import axios, {
	AxiosError,
	AxiosInstance,
	AxiosRequestConfig,
	AxiosRequestHeaders,
	AxiosResponse,
	CancelToken,
} from "axios";

import { MS_IN, MS_IN_SECOND } from "./constants";
import { hasValue } from "./general";

const DEFAULT_TIMEOUT = 5 * MS_IN.MINUTE;

/**
 * Make a POST request
 * @param url
 * @param params
 * @param data
 * @param config
 */
export async function Post<TOut, TIn>(url: string, params?: object | string, data?: TIn, config?: AxiosRequestConfig) {
	return await execute((a) => a.post<TOut>(url, data, { params, ...config }));
}

export async function Put<TOut, TIn>(url: string, params?: object | string, data?: TIn, config?: AxiosRequestConfig) {
	return await execute((a) => a.put<TOut>(url, data, { params, ...config }));
}

/**
 * Make a GET request
 * @param url
 * @param params
 */
export async function Get<TOut>(url: string, params?: object | string): Promise<TOut> {
	return await execute((a) => a.get<TOut>(url, { params, paramsSerializer: { indexes: true } }));
}

export async function Delete<TOut>(url: string, id: number) {
	if (url[url.length - 1] !== "/") {
		url += "/";
	}
	if (hasValue(id)) url += id;

	return await execute((a) => a.delete<TOut>(url));
}

/** Fires a ping (Get) with a 1-second timeout to verify connection */
export async function Ping<TOut>(url: string) {
	return await execute((a) => a.get<TOut>(url), {}, MS_IN_SECOND);
}

async function execute<TOut>(
	fn: (api: AxiosInstance) => Promise<AxiosResponse<TOut>>,
	config: AxiosRequestConfig = {},
	timeoutMS?: number,
) {
	const cancelSource = axios.CancelToken.source();
	const api = getApi(config, cancelSource.token);

	const timeout = setTimeout(() => cancelSource.cancel(), timeoutMS ?? DEFAULT_TIMEOUT);

	try {
		const response = await fn(api);
		return response?.data;
	} catch (error) {
		throw handleError(error);
	} finally {
		clearTimeout(timeout);
	}
}

function handleError(error: AxiosError<any>) {
	error = error || ({} as AxiosError<any>);
	const resp = error.response || ({} as AxiosResponse<any>);
	return {
		status: resp.status,
		message: resp.data || error.message,
	} as HttpError;
}

/** Sets up and returns Axios instance */
function getApi(config: AxiosRequestConfig = {}, cancelToken?: CancelToken) {
	config.headers = config.headers || ({} as AxiosRequestHeaders);
	config.headers["No-Auth-Redirect"] = "NoAuthRedirect";
	return axios.create({ ...config, withCredentials: true, cancelToken });
}

export interface HttpError {
	status: number;
	message: string;
}

export default { Post, Put, Get, Delete, Ping };
